import { useEffect, useRef, useState, type CSSProperties } from 'react';
import styled from 'styled-components';
import { Button } from './Button';

const Wrap = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const Display = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const PencilBtn = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.muted};
  cursor: pointer;
  padding: 2px 4px;
  font-size: 11px;
  border-radius: ${({ theme }) => theme.radius.sm};
  transition:
    color 160ms ease,
    background-color 160ms ease;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const EditorRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const InputEl = styled.input`
  padding: 4px 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;
  min-width: 200px;
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const SelectEl = styled.select`
  padding: 4px 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;
`;

const ErrorText = styled.span`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 11px;
  margin-left: 6px;
`;

interface BaseProps {
  value: string;
  onSave: (raw: string) => Promise<void>;
  /** What to render when not editing. Defaults to value or "—". */
  display?: React.ReactNode;
  placeholder?: string;
  busy?: boolean;
  style?: CSSProperties;
}

type Props =
  | (BaseProps & { kind?: 'text' })
  | (BaseProps & { kind: 'date' })
  | (BaseProps & { kind: 'number'; step?: string })
  | (BaseProps & {
      kind: 'select';
      options: Array<{ value: string; label: string }>;
    });

/**
 * Inline-edit для строкового / числового / датового / select-поля.
 * Используется в карточке договора, чтобы юрист мог переопределять поля,
 * извлечённые LLM (signed_at, sum, тип, контрагент и т.д.).
 *
 * Поведение: клик по карандашу → input с автофокусом. Enter — сохранить,
 * Esc — отменить. Снаружи передаётся `onSave(raw)` — Promise, который
 * вернётся когда мутация завершится.
 */
export function InlineEdit(props: Props) {
  const { value, onSave, display, placeholder, busy, style } = props;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null);

  useEffect(() => {
    if (editing) setDraft(value);
  }, [editing, value]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  async function save() {
    setError(null);
    try {
      await onSave(draft.trim());
      setEditing(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Ошибка сохранения';
      setError(msg);
    }
  }

  function cancel() {
    setDraft(value);
    setEditing(false);
    setError(null);
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      void save();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  }

  if (!editing) {
    return (
      <Wrap style={style}>
        <Display>{display ?? value ?? '—'}</Display>
        <PencilBtn
          type="button"
          onClick={() => setEditing(true)}
          title="Изменить"
          aria-label="Изменить"
        >
          ✎
        </PencilBtn>
      </Wrap>
    );
  }

  return (
    <EditorRow style={style}>
      {props.kind === 'select' ? (
        <SelectEl
          ref={(el) => {
            inputRef.current = el;
          }}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
        >
          {props.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </SelectEl>
      ) : (
        <InputEl
          ref={(el) => {
            inputRef.current = el;
          }}
          type={props.kind === 'date' ? 'date' : props.kind === 'number' ? 'number' : 'text'}
          step={props.kind === 'number' ? (props.step ?? 'any') : undefined}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          placeholder={placeholder}
        />
      )}
      <Button type="button" $size="sm" onClick={() => void save()} disabled={busy}>
        {busy ? '…' : '✓'}
      </Button>
      <Button type="button" $size="sm" $variant="secondary" onClick={cancel}>
        ✕
      </Button>
      {error && <ErrorText>{error}</ErrorText>}
    </EditorRow>
  );
}
